import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Calculator, Filter, X, Search, RefreshCcw, Layers,
} from "lucide-react";

const GROUPS = [
  { key: "brand_size", label: "Brand + Size" },
  { key: "brand",      label: "Brand" },
  { key: "size",       label: "Size" },
  { key: "customer",   label: "Customer" },
  { key: "godown",     label: "Godown" },
  { key: "month",      label: "Month" },
  { key: "none",       label: "No grouping" },
];

export default function SaleCalculatorPage() {
  const { auth }   = useAuth();
  const company    = auth?.company || {};
  const godowns    = (company.godowns || []).filter((g) => !/bullet/i.test(g));

  const [brands,    setBrands]    = useState([]);
  const [sizes,     setSizes]     = useState([]);
  const [customers, setCustomers] = useState([]);

  const [selBrand,    setSelBrand]    = useState("");
  const [selSize,     setSelSize]     = useState("");
  const [selGodown,   setSelGodown]   = useState("");
  const [selCustomer, setSelCustomer] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo]     = useState(todayISO);
  const [groupBy, setGroupBy] = useState("brand_size");
  const [search, setSearch]   = useState("");
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, c] = await Promise.all([
          api.get("/masters"),
          api.get("/customers?customer_type=customer"),
        ]);
        setBrands((m.data?.brands || []).map((b) => b.name || b));
        setSizes((m.data?.sizes   || []).map((s) => s.name || s));
        setCustomers(c.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/sale-calculator", {
        params: {
          brand:       selBrand    || undefined,
          size:        selSize     || undefined,
          godown:      selGodown   || undefined,
          customer_id: selCustomer || undefined,
          date_from:   from        || undefined,
          date_to:     to          || undefined,
          group_by:    groupBy,
        },
      });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selBrand, selSize, selGodown, selCustomer, from, to, groupBy]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data?.groups || [];
    return (data?.groups || []).filter((g) => g.key.toLowerCase().includes(q));
  }, [data, search]);

  const totals     = data?.totals     || {};
  const groupLabel = GROUPS.find((g) => g.key === groupBy)?.label || "";

  const clearFilters = () => { setSelBrand(""); setSelSize(""); setSelGodown(""); setSelCustomer(""); setSearch(""); };
  const hasFilter    = selBrand || selSize || selGodown || selCustomer;

  const quickRange = (days) => () => {
    const t = new Date(), f = new Date();
    f.setDate(t.getDate() - days);
    setFrom(f.toISOString().slice(0, 10));
    setTo(t.toISOString().slice(0, 10));
  };
  const thisMonth = () => {
    const t = new Date();
    setFrom(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10));
    setTo(todayISO());
  };
  const lastMonth = () => {
    const t = new Date();
    setFrom(new Date(t.getFullYear(), t.getMonth() - 1, 1).toISOString().slice(0, 10));
    setTo(new Date(t.getFullYear(), t.getMonth(), 0).toISOString().slice(0, 10));
  };

  const ratePerKg = (totals.kg && totals.kg > 0)
    ? (totals.amount / totals.kg).toFixed(2)
    : null;

  return (
    <div className="space-y-6" data-testid="sale-calc-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Calculator size={22} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Sale Calculator</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Compute totals by month · customer · brand · size · godown
        </span>
        <button onClick={load}
          className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black"
          data-testid="sc-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card-flat p-5 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <div>
            <label className="small-label block mb-1.5">Customer</label>
            <select className="input-lg h-9 text-sm" value={selCustomer}
              onChange={(e) => setSelCustomer(e.target.value)} data-testid="sc-party">
              <option value="">All customers</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1.5">Brand</label>
            <select className="input-lg h-9 text-sm" value={selBrand}
              onChange={(e) => setSelBrand(e.target.value)} data-testid="sc-brand">
              <option value="">All brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1.5">Size</label>
            <select className="input-lg h-9 text-sm" value={selSize}
              onChange={(e) => setSelSize(e.target.value)} data-testid="sc-size">
              <option value="">All sizes</option>
              {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1.5">Godown</label>
            <select className="input-lg h-9 text-sm" value={selGodown}
              onChange={(e) => setSelGodown(e.target.value)} data-testid="sc-godown">
              <option value="">All godowns</option>
              {godowns.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="small-label block mb-1.5">From</label>
            <input type="date" className="input-lg h-9 text-sm" value={from}
              onChange={(e) => setFrom(e.target.value)} data-testid="sc-from" />
          </div>
          <div>
            <label className="small-label block mb-1.5">To</label>
            <input type="date" className="input-lg h-9 text-sm" value={to}
              onChange={(e) => setTo(e.target.value)} data-testid="sc-to" />
          </div>
          <div>
            <label className="small-label block mb-1.5 flex items-center gap-1">
              <Layers size={11} /> Group by
            </label>
            <select className="input-lg h-9 text-sm" value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)} data-testid="sc-group">
              {GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500">
          <span>Quick:</span>
          <button onClick={thisMonth} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-this">This month</button>
          <button onClick={lastMonth} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-last">Last month</button>
          <button onClick={quickRange(7)} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-7">Last 7d</button>
          <button onClick={quickRange(30)} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-30">Last 30d</button>
          <button onClick={quickRange(90)} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-90">Last 90d</button>
          {hasFilter && (
            <>
              <Filter size={11} className="ml-2 text-gray-400" />
              {selBrand && <button onClick={() => setSelBrand("")} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200 text-[11px]">Brand: {selBrand} <X size={10} /></button>}
              {selSize  && <button onClick={() => setSelSize("")}  className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200 text-[11px]">Size: {selSize}   <X size={10} /></button>}
              {selGodown && <button onClick={() => setSelGodown("")} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200 text-[11px]">Godown: {selGodown} <X size={10} /></button>}
              {selCustomer && <button onClick={() => setSelCustomer("")} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200 text-[11px]">{customers.find((c) => c.id === selCustomer)?.name || "Customer"} <X size={10} /></button>}
              <button onClick={clearFilters} className="text-[11px] font-semibold text-[#B91C1C] hover:underline ml-1" data-testid="sc-clear">Clear all</button>
            </>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70">Total sales</div>
          <div className="data-num text-3xl font-black mt-1" data-testid="sc-kpi-amount">₹ {fmt(totals.amount || 0)}</div>
          <div className="text-[10px] text-white/70 mt-1">{totals.bills || 0} bills</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Total KG (LPG)</div>
          <div className="data-num text-3xl font-black mt-1 text-[#B45309]" data-testid="sc-kpi-kg">{fmt(totals.kg || 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Filled cylinders</div>
          <div className="data-num text-2xl mt-1" data-testid="sc-kpi-filled">{fmt(totals.filled_qty || 0, 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">MT cylinders</div>
          <div className="data-num text-2xl mt-1" data-testid="sc-kpi-mt">{fmt(totals.mt_qty || 0, 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Avg ₹ / kg</div>
          <div className="data-num text-2xl mt-1" data-testid="sc-kpi-rpk">
            {ratePerKg ? `₹ ${ratePerKg}` : "—"}
          </div>
        </div>
      </div>

      {/* Group table */}
      {groupBy !== "none" && (
        <div className="card-flat" data-testid="sc-group-block">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="small-label text-gray-500">Breakdown by</div>
              <div className="font-heading font-black text-lg">{groupLabel}</div>
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search group…" className="input-lg h-9 text-sm pl-8 w-56"
                data-testid="sc-group-search" />
            </div>
          </div>
          <div className="table-wrap">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">{groupLabel}</th>
                  <th className="num">Bills</th>
                  <th className="num">Filled</th>
                  <th className="num">MT</th>
                  <th className="num">KG</th>
                  <th className="num">₹ / kg</th>
                  <th className="num">Amount ₹</th>
                  <th className="num">Share</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>
                )}
                {!loading && groups.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-sm text-gray-400">No sales in this window.</td></tr>
                )}
                {!loading && groups.map((g) => {
                  const share = totals.amount > 0 ? (g.amount / totals.amount) * 100 : 0;
                  return (
                    <tr key={g.key} data-testid={`sc-group-${g.key}`}>
                      <td className="font-semibold">{g.key}</td>
                      <td className="num text-xs text-gray-500">{g.bills}</td>
                      <td className="num data-num text-[#15803D] font-bold">{g.filled_qty || ""}</td>
                      <td className="num data-num text-[#B45309] font-bold">{g.mt_qty || ""}</td>
                      <td className="num data-num">{fmt(g.kg)}</td>
                      <td className="num data-num text-xs">₹ {g.rate_per_kg}</td>
                      <td className="num data-num font-bold">₹ {fmt(g.amount)}</td>
                      <td className="num">
                        <div className="flex items-center gap-2 justify-end">
                          <span className="text-xs text-gray-500 w-10 text-right">{share.toFixed(1)}%</span>
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-black" style={{ width: `${Math.min(share, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500 p-3 border-t border-gray-100">
            Aggregates cover only line items that match the active brand / size filter.
            <Link to="/logs/sales" className="ml-2 underline font-semibold">Open full sales log →</Link>
          </p>
        </div>
      )}
    </div>
  );
}
